import { useCallback } from 'react';
import { CopyIcon, RefreshCwIcon } from 'lucide-react';
import type { RequestLog } from '@mockd/shared';
import toast from 'react-hot-toast';
import { Cursor } from '../common/Cursor';
import { MethodBadge } from '../common/MethodBadge';
import { StatusBadge } from '../common/StatusBadge';
import { parseMatchedRules, resolveRuleName } from './matchedRules';
import type { ReplayState } from '../../hooks/useRequestReplay';

interface RequestItemProps {
  request: RequestLog;
  mockBaseUrl?: string;
  replayState?: ReplayState;
  onReplay?: (request: RequestLog) => void;
  isSelected?: boolean;
  onSelect?: () => void;
  /** Rule id → current name, for resolving matched_rule_ids. */
  ruleNames?: ReadonlyMap<string, string>;
}

function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) {
    return `${diffSec}s ago`;
  }

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) {
    return `${diffMin}m ago`;
  }

  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) {
    return `${diffHr}h ago`;
  }

  const diffDays = Math.floor(diffHr / 24);
  if (diffDays < 30) {
    return `${diffDays}d ago`;
  }

  const diffMonths = Math.floor(diffDays / 30);
  return `${diffMonths}mo ago`;
}

function buildFullCurlCommand(request: RequestLog, mockBaseUrl: string): string {
  const parts = [`curl -X ${request.method}`];

  try {
    const headers = JSON.parse(request.headers || '{}');
    for (const [key, value] of Object.entries(headers)) {
      const lower = key.toLowerCase();
      if (lower === 'host' || lower === 'content-length' || lower.startsWith('cf-')) continue;
      parts.push(`-H '${key}: ${value}'`);
    }
  } catch {
    // skip malformed headers
  }

  if (request.body && request.body !== 'null') {
    try {
      const parsed = JSON.parse(request.body);
      parts.push(`-d '${JSON.stringify(parsed)}'`);
    } catch {
      parts.push(`-d '${request.body}'`);
    }
  }

  parts.push(`'${mockBaseUrl}${request.path}'`);
  return parts.join(' \\\n  ');
}

function MatchedRulesBadge({ request, ruleNames }: { request: RequestLog; ruleNames?: ReadonlyMap<string, string> }) {
  const matched = parseMatchedRules(request);
  if (matched.length === 0) {
    return <span className="badge badge-ghost hidden sm:inline-flex">No rule matched</span>;
  }

  const title = matched
    .map((r, i) => `${i + 1}. ${resolveRuleName(r.id, ruleNames, request) ?? 'Deleted rule'}${r.capturedResponse ? ' (response)' : ''}`)
    .join('\n');
  const responseRule = matched.find((r) => r.capturedResponse);
  if (!responseRule) {
    return (
      <span className="badge badge-ghost hidden sm:inline-flex" title={title}>
        {matched.length} {matched.length === 1 ? 'rule' : 'rules'}, no response
      </span>
    );
  }

  const others = matched.length - 1;
  return (
    <span className="badge badge-secondary hidden sm:inline-flex" title={title}>
      {resolveRuleName(responseRule.id, ruleNames, request) ?? 'Rule'}
      {others > 0 && ` +${others}`}
    </span>
  );
}

export function RequestItem({ request, mockBaseUrl, replayState, onReplay, isSelected, onSelect, ruleNames }: RequestItemProps) {
  const handleReplay = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onReplay?.(request);
  }, [onReplay, request]);

  const handleCopyCurl = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!mockBaseUrl) return;
    try {
      await navigator.clipboard.writeText(buildFullCurlCommand(request, mockBaseUrl));
      toast.success('cURL command copied');
    } catch {
      toast.error('Copy failed. Try using HTTPS.');
    }
  }, [request, mockBaseUrl]);

  const isReplaying = replayState?.isReplaying ?? false;
  const hasReplayResult = (replayState?.response ?? null) !== null || (replayState?.error ?? null) !== null;

  return (
    <div className="border-b border-base-200 last:border-b-0">
      <div
        role="button"
        tabIndex={0}
        onClick={() => onSelect?.()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect?.();
          }
        }}
        className={`w-full px-4 py-3 flex items-center gap-4 transition-colors text-left cursor-pointer border-l-2 ${
          isSelected
            ? 'bg-primary/10 border-l-primary'
            : 'hover:bg-base-200 border-l-transparent'
        }`}
      >
        <MethodBadge method={request.method} />
        <StatusBadge status={request.response_status} />
        <span className="flex-1 font-mono text-sm text-base-content truncate">
          {request.path}
        </span>
        <MatchedRulesBadge request={request} ruleNames={ruleNames} />
        {request.response_time_ms !== null && (
          <span className="text-xs text-base-content/70 font-mono hidden sm:inline">
            {request.response_time_ms}ms
          </span>
        )}

        {/* Action buttons */}
        {mockBaseUrl && (
          <span className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
            <button
              onClick={handleReplay}
              disabled={isReplaying}
              className="btn btn-ghost btn-xs"
              title="Replay request"
              aria-label="Replay request"
            >
              {isReplaying ? (
                <Cursor />
              ) : (
                <RefreshCwIcon className={`size-3.5 ${hasReplayResult ? 'text-success' : ''}`} />
              )}
            </button>
            <button
              onClick={handleCopyCurl}
              className="btn btn-ghost btn-xs"
              title="Copy as cURL"
              aria-label="Copy as cURL"
            >
              <CopyIcon className="size-3.5" />
            </button>
          </span>
        )}

        <span className="text-xs text-base-content/70">
          {formatTimestamp(request.timestamp)}
        </span>
      </div>

    </div>
  );
}
